import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

const {FiPlay, FiEye, FiShield, FiUsers, FiHeart} = FiIcons;

const FeaturedVideo = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-purple-900 to-gray-900">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
            Welcome to LRCS
          </h2>
          <p className="text-xl text-gray-300">
            Meet Linda Roberts and discover how she's protecting millions from scammers
          </p>
        </motion.div>

        <motion.div
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="relative group">
            <div className="aspect-video bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border border-purple-500/30">
              {/* Gumlet Video Embed */}
              <div style={{position: 'relative', aspectRatio: '16/9'}}>
                <iframe 
                  loading="lazy" 
                  title="Welcome to Linda Roberts Calls Scammers - LRCS Introduction"
                  src="https://play.gumlet.io/embed/688abc3f5aebf9537719278d?background=false&autoplay=false&loop=false&disableControls=false"
                  style={{border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%'}}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen;"
                />
              </div>
            </div>

            {/* Video description */}
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                "Meet the Scammer's Worst Nightmare"
              </h3>
              <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed mb-6">
                Linda Roberts Calls Scammers (LRCS) is more than entertainment—it's education in action. Watch Linda Roberts, a street-smart New Yorker with unmatched sass, turn the tables on internet fraudsters while teaching millions how to protect themselves from online scams.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                  <SafeIcon icon={FiShield} className="text-2xl text-pink-500 mb-3 mx-auto" />
                  <h4 className="font-bold text-white mb-2">Scam Education</h4>
                  <p className="text-gray-400 text-sm">Learn to identify and avoid romance, tech support, IRS, and phishing scams through real-world examples</p>
                </div>
                <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                  <SafeIcon icon={FiUsers} className="text-2xl text-purple-500 mb-3 mx-auto" />
                  <h4 className="font-bold text-white mb-2">Community Impact</h4>
                  <p className="text-gray-400 text-sm">Join millions of viewers who've learned to protect themselves and their families from cybercrime</p>
                </div>
                <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700">
                  <SafeIcon icon={FiHeart} className="text-2xl text-cyan-500 mb-3 mx-auto" />
                  <h4 className="font-bold text-white mb-2">Victim Support</h4>
                  <p className="text-gray-400 text-sm">Access resources and support through AVAH Outreach Group for scam recovery and prevention</p>
                </div>
              </div>

              <div className="bg-gray-800 p-8 rounded-2xl border border-pink-500/30 max-w-2xl mx-auto mb-8">
                <h4 className="text-xl font-bold text-white mb-4">Why LRCS Matters</h4>
                <p className="text-gray-300 leading-relaxed mb-4">
                  In an era where cybercrime costs Americans billions annually, Linda Roberts Calls Scammers serves as both shield and sword—protecting potential victims while exposing fraudulent operations. Through authentic scam baiting content, LRCS educates viewers on:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left">
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="text-green-400 text-sm" />
                    <span className="text-gray-300 text-sm">Romance scam red flags</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="text-green-400 text-sm" />
                    <span className="text-gray-300 text-sm">Tech support fraud tactics</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="text-green-400 text-sm" />
                    <span className="text-gray-300 text-sm">IRS impersonation schemes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="text-green-400 text-sm" />
                    <span className="text-gray-300 text-sm">Phishing email identification</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center gap-6 text-sm text-gray-400 mb-6">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiEye} />
                  <span>Millions of views</span>
                </div>
                <div>•</div>
                <div>Trusted by cybersecurity experts</div>
                <div>•</div>
                <div>Featured in digital safety education</div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="http://link.lrcs.nyc/YTSL" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-3 shadow-lg hover:shadow-pink-500/25"
                >
                  <SafeIcon icon={FiPlay} className="text-xl" />
                  Subscribe on YouTube
                </a>
                <Link 
                  to="/scam-assistance"
                  className="bg-transparent border-2 border-cyan-400 hover:bg-cyan-400 hover:text-gray-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-3"
                >
                  <SafeIcon icon={FiShield} className="text-xl" />
                  Get Scam Help
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedVideo;